import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  Keyboard,
  X
} from '@phosphor-icons/react';

const shortcuts = [
  { category: 'Navegacion', items: [
    { keys: ['G', 'D'], description: 'Ir al Dashboard' },
    { keys: ['G', 'V'], description: 'Ir a Vulnerabilidades' },
    { keys: ['G', 'A'], description: 'Ir a Agentes' },
    { keys: ['G', 'R'], description: 'Ir a Reportes' },
    { keys: ['G', 'N'], description: 'Ir a Mapa de Red' },
    { keys: ['G', 'S'], description: 'Ir a Configuracion' },
  ]},
  { category: 'Acciones Rapidas', items: [
    { keys: ['Ctrl/Cmd', 'K'], description: 'Busqueda global' },
    { keys: ['Shift', '?'], description: 'Mostrar atajos de teclado' },
    { keys: ['Esc'], description: 'Cerrar modal/panel' },
    { keys: ['R'], description: 'Refrescar datos' },
  ]},
  { category: 'Vulnerabilidades', items: [
    { keys: ['1'], description: 'Vista Mapa' },
    { keys: ['2'], description: 'Vista Lista' },
    { keys: ['3'], description: 'Vista Timeline' },
    { keys: ['E'], description: 'Exportar CSV' },
  ]},
  { category: 'Dashboard', items: [
    { keys: ['C'], description: 'Ver CVEs criticas' },
    { keys: ['A'], description: 'Ver todos los agentes' },
  ]},
];

const KeyboardShortcuts = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);
  const navigate = useNavigate();

  const handleKeyDown = useCallback((e) => {
    // Don't trigger shortcuts when typing in inputs
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }

    // Show keyboard shortcuts modal with Shift + ?
    if (e.shiftKey && e.key === '?') {
      e.preventDefault();
      setIsOpen(prev => !prev);
      return;
    }

    // Close modal with Escape
    if (e.key === 'Escape') {
      setIsOpen(false);
      setPendingKey(null);
      return;
    }

    // Handle "G" prefix for navigation
    if (e.key.toLowerCase() === 'g' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      setPendingKey('g');
      setTimeout(() => setPendingKey(null), 1500); // Reset after 1.5s
      return;
    }

    // Handle navigation shortcuts (G + key)
    if (pendingKey === 'g') {
      const key = e.key.toLowerCase();
      switch (key) {
        case 'd':
          navigate('/dashboard');
          break;
        case 'v':
          navigate('/vulnerabilities');
          break;
        case 'a':
          navigate('/agents');
          break;
        case 'r':
          navigate('/reports');
          break;
        case 'n':
          navigate('/network');
          break;
        case 's':
          navigate('/settings');
          break;
        default:
          break;
      }
      setPendingKey(null);
      return;
    }

    // Ctrl/Cmd + K abre la paleta de comandos: lo gestiona CommandPalette.
    // Aquí solo se corta para que no caiga en los atajos de página.
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      return;
    }

    // Handle standalone shortcuts on specific pages
    const path = window.location.pathname;

    if (path.includes('/vulnerabilities')) {
      switch (e.key) {
        case '1':
          // Switch to map view - dispatch custom event
          window.dispatchEvent(new CustomEvent('setViewMode', { detail: 'map' }));
          break;
        case '2':
          window.dispatchEvent(new CustomEvent('setViewMode', { detail: 'list' }));
          break;
        case '3':
          window.dispatchEvent(new CustomEvent('setViewMode', { detail: 'timeline' }));
          break;
        case 'e':
        case 'E':
          if (!e.ctrlKey && !e.metaKey) {
            window.dispatchEvent(new CustomEvent('exportCSV'));
          }
          break;
        default:
          break;
      }
    }

    if (path.includes('/dashboard')) {
      switch (e.key.toLowerCase()) {
        case 'c':
          navigate('/vulnerabilities?severity=critical');
          break;
        case 'a':
          if (pendingKey !== 'g') {
            navigate('/agents');
          }
          break;
        default:
          break;
      }
    }

    // Refresh data
    if (e.key.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey) {
      window.dispatchEvent(new CustomEvent('refreshData'));
    }
  }, [navigate, pendingKey]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) {
    return (
      <>
        {/* Pending key indicator */}
        {pendingKey && (
          <div className="fixed bottom-4 right-4 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 z-50 flex items-center gap-2">
            <kbd className="bg-slate-700 px-2 py-1 rounded text-v4-txt font-mono text-sm">
              {pendingKey.toUpperCase()}
            </kbd>
            <span className="text-slate-400 text-sm">esperando siguiente tecla...</span>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="bg-v4-panel-2 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-v4-panel-2 border-b border-slate-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
              <Keyboard className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-v4-txt">Atajos de Teclado</h2>
              <p className="text-sm text-slate-400">Navega mas rapido con el teclado</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-slate-400 hover:text-v4-txt transition-colors p-2"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {shortcuts.map((section, idx) => (
            <div key={idx}>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                {section.category}
              </h3>
              <div className="space-y-2">
                {section.items.map((shortcut, sIdx) => (
                  <div
                    key={sIdx}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-800/50 transition-colors"
                  >
                    <span className="text-v4-txt">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, kIdx) => (
                        <React.Fragment key={kIdx}>
                          {kIdx > 0 && <span className="text-slate-500 mx-1">+</span>}
                          <kbd className="bg-slate-700 border border-slate-600 px-2.5 py-1 rounded text-sm font-mono text-v4-txt min-w-[28px] text-center">
                            {key}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-v4-panel-2 border-t border-slate-700 p-4">
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Command className="h-4 w-4" />
            <span>Presiona</span>
            <kbd className="bg-slate-700 px-2 py-0.5 rounded text-xs font-mono">Shift</kbd>
            <span>+</span>
            <kbd className="bg-slate-700 px-2 py-0.5 rounded text-xs font-mono">?</kbd>
            <span>para mostrar/ocultar</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcuts;
